import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { getArticleById } from '@/mocks/article';
import { categoryInfo } from '@/mocks/category';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = useMemo(() => (slug ? getArticleById(slug) : null), [slug]);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-16 md:pt-18 min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <span className="w-16 h-16 flex items-center justify-center rounded-2xl bg-background-100 text-foreground-400 text-2xl mx-auto mb-4">
              <i className="ri-file-unknow-line" />
            </span>
            <h2 className="font-heading text-xl font-bold text-foreground-900 mb-2">文章未找到</h2>
            <p className="text-sm text-foreground-500 mb-4">该文章可能已被删除或链接有误</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line" />
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const catInfo = categoryInfo[article.categorySlug];

  return (
    <>
      <Navbar />
      <main className="pt-16 md:pt-18">
        {/* Hero Image */}
        <section className="relative w-full h-[300px] md:h-[420px] overflow-hidden">
          <img
            src={article.image}
            alt={article.title}
            className="w-full h-full object-cover object-top"
            title={`${article.title} - 吃瓜网站`}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/10 to-black/60" />
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <span className="px-2.5 py-1 rounded-full bg-primary-500 text-background-50 text-xs font-semibold">
                  {article.category}
                </span>
                {article.tag && (
                  <span className="px-2 py-1 rounded-md bg-accent-500 text-background-50 text-[10px] font-bold">
                    {article.tag}
                  </span>
                )}
              </div>
              <h1 className="font-heading text-2xl md:text-4xl font-bold text-background-50 leading-tight">
                {article.title}
              </h1>
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            {/* Breadcrumb */}
            <nav className="flex items-center flex-wrap gap-2 text-xs text-foreground-500 mb-6" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-500 transition-colors">首页</Link>
              <i className="ri-arrow-right-s-line text-[10px]" />
              {catInfo ? (
                <Link
                  to={`/category/${article.categorySlug}`}
                  className="hover:text-primary-500 transition-colors"
                >
                  {catInfo.name}
                </Link>
              ) : (
                <span>{article.category}</span>
              )}
              <i className="ri-arrow-right-s-line text-[10px]" />
              <span className="text-primary-500 font-medium truncate max-w-[200px]">{article.title}</span>
            </nav>

            {/* Meta */}
            <div className="flex flex-wrap items-center gap-4 mb-8 pb-6 border-b border-background-200">
              <div className="flex items-center gap-2">
                <span className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-100 text-primary-500 text-xs font-bold">
                  {article.author.charAt(0)}
                </span>
                <div>
                  <p className="text-sm font-medium text-foreground-900">{article.author}</p>
                  <p className="text-[11px] text-foreground-400">{article.authorBio}</p>
                </div>
              </div>
              <div className="flex items-center gap-4 ml-auto text-xs text-foreground-400">
                <span className="flex items-center gap-1">
                  <i className="ri-time-line" />
                  {article.date}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line" />
                  {article.views} 阅读
                </span>
              </div>
            </div>

            {/* Article body */}
            <article
              className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{
                __html: article.content
                  .replace(/<p><strong>/g, '<p class="font-semibold text-foreground-900 mt-8 mb-3 text-base md:text-lg"><strong>')
                  .replace(/<p>/g, '<p class="mb-4 text-sm md:text-base leading-relaxed">'),
              }}
            />

            {/* Tags */}
            <div className="flex flex-wrap items-center gap-2 mt-10 pt-6 border-t border-background-200">
              <span className="text-xs text-foreground-500 mr-1">标签：</span>
              {article.tags.map((tag) => (
                <Link
                  key={tag}
                  to={`/category/${article.categorySlug}`}
                  className="px-3 py-1 rounded-full bg-background-100 text-xs text-foreground-600 hover:bg-primary-50 hover:text-primary-500 transition-colors cursor-pointer"
                >
                  #{tag}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Related Articles */}
        {article.relatedArticles.length > 0 && (
          <section className="py-10 md:py-14 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="flex items-center gap-3 mb-8">
                <span className="w-1 h-5 rounded-full bg-primary-500" />
                <h2 className="font-heading text-xl font-bold text-foreground-950">相关推荐</h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {article.relatedArticles.map((rel) => (
                  <Link
                    key={rel.id}
                    to={`/article/${rel.id}`}
                    className="bg-background-50 rounded-xl overflow-hidden group cursor-pointer"
                  >
                    <div className="relative w-full aspect-[4/3] overflow-hidden">
                      <img
                        src={rel.image}
                        alt={rel.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                        title={`${rel.title} - 吃瓜网站相关推荐`}
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="font-heading text-sm font-semibold text-foreground-900 line-clamp-2 leading-snug mb-2 group-hover:text-primary-500 transition-colors">
                        {rel.title}
                      </h3>
                      <div className="flex items-center gap-3 text-[11px] text-foreground-400">
                        <span className="flex items-center gap-1">
                          <i className="ri-time-line" />
                          {rel.date}
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-eye-line" />
                          {rel.views}
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Schema.org structured data */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'Article',
              headline: article.title,
              description: article.excerpt,
              image: article.image,
              datePublished: article.date,
              author: {
                '@type': 'Person',
                name: article.author,
              },
              publisher: {
                '@type': 'Organization',
                name: '吃瓜网站',
                url: 'https://chigua.example.com',
              },
              mainEntityOfPage: {
                '@type': 'WebPage',
                '@id': `https://chigua.example.com/article/${article.id}`,
              },
            }),
          }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'BreadcrumbList',
              itemListElement: [
                {
                  '@type': 'ListItem',
                  position: 1,
                  name: '首页',
                  item: 'https://chigua.example.com',
                },
                {
                  '@type': 'ListItem',
                  position: 2,
                  name: article.category,
                  item: `https://chigua.example.com/category/${article.categorySlug}`,
                },
                {
                  '@type': 'ListItem',
                  position: 3,
                  name: article.title,
                  item: `https://chigua.example.com/article/${article.id}`,
                },
              ],
            }),
          }}
        />
      </main>
      <Footer />
    </>
  );
}